import React, { createContext, useContext, useEffect, useState } from 'react';
import usePWA from '../hooks/usePWA';
import PWAInstallModal from './PWAInstallModal';

const PWAContext = createContext();

// eslint-disable-next-line react-refresh/only-export-components -- context hook co-located with its provider component (established convention; dev-only HMR hint)
export const usePWAContext = () => {
  const context = useContext(PWAContext);
  if (!context) {
    throw new Error('usePWAContext must be used within a PWAProvider');
  }
  return context;
};

// Same context, null outside a PWAProvider (component tests, isolated renders).
// eslint-disable-next-line react-refresh/only-export-components -- context hook co-located with its provider component
export const useOptionalPWAContext = () => useContext(PWAContext) ?? null;

const PWAProvider = ({ children }) => {
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [hasShownInstallPrompt, setHasShownInstallPrompt] = useState(false);
  
  const {
    isInstallable,
    isInstalled,
    needRefresh,
    installPWA,
    updateApp,
    // usePWA's real fields: Chrome's install prompt is held and can be shown.
    showInstallPrompt: canPromptInstall,
  } = usePWA();

  // Show install prompt after a delay if app is installable and not already installed
  useEffect(() => {
    if (isInstallable && !isInstalled && !hasShownInstallPrompt) {
      const timer = setTimeout(() => {
        setShowInstallModal(true);
        setHasShownInstallPrompt(true);
      }, 3000); // Show after 3 seconds

      return () => clearTimeout(timer);
    }
  }, [isInstallable, isInstalled, hasShownInstallPrompt]);

  const handleInstall = async () => {
    const success = await installPWA();
    if (success) {
      setShowInstallModal(false);
    }
  };

  const handleCloseModal = () => {
    setShowInstallModal(false);
  };

  const contextValue = {
    isInstallable,
    isInstalled,
    needRefresh,
    installPWA: handleInstall,
    updateApp,
    showInstallModal,
    setShowInstallModal,
    // For an explicit « Installer » button (student onboarding): true once
    // Chrome/Android has offered the install, `promptInstall` opens it.
    canPromptInstall: Boolean(canPromptInstall),
    promptInstall: installPWA,
  };

  return (
    <PWAContext.Provider value={contextValue}>
      {children}
      
      {/* PWA Install Modal */}
      {showInstallModal && (
        <PWAInstallModal
          isOpen={showInstallModal}
          onClose={handleCloseModal}
          onInstall={handleInstall}
        />
      )}
    </PWAContext.Provider>
  );
};

export default PWAProvider;
